
import moment from 'moment'
import numbro from 'numbro'

import CreateOptimization from '@/components/CreateOptimization.vue'
import CompareSimulations from '@/components/CompareSimulations.vue'
import CreatePlanChooser from '@/components/CreatePlanChooser.vue'
import Simulation from '@/components/Simulation.vue'

import { mapGetters, mapActions } from 'vuex'
import { driver } from '@/mixins/driver.js'
import { ngtorchRuns } from '@/mixins/ngtorchRuns.js'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import { loadModelReportFromClient } from '@/utils/modelReport.js'
// The Plan Optimizer intentionally does NOT apply the legacy-weekly push-forward,
// so it clamps template windows to the raw model start (not the effective one).
import { clampMonthlyStart, clampWeeklyStart } from '@/utils/modelStart.js'
import { isSpendUpTo, investedBudget } from '@/utils/optimizerBudget.js'
import { hasHiddenKpis, firstVisibleOutputName } from '@/utils/hiddenKpis.js'
import { calculatorRange, planAxis, buildPrefillLookup, buildFlexibleMediaPlan, flexibleBounds as dataBounds } from '@/utils/flexibleMediaPlan.js'

import _ from 'lodash'

export default {
  mixins: [driver, ngtorchRuns],
  data () {
    return {
      search: '',
      dialog: false,
      chooser: false,
      baseSimulation: null,
      edit: false,
      compare: false,
      selectedSimulation: {},
      expandedSimulations: [],
      isTemplate: false,
      fetchedModelReport: null,
      fetchedModelReportFor: null,
      mismatchDialog: false,
      pendingBase: null,
      pendingEdit: false,
      mismatchInfo: { removed: [] }
    }
  },
  components: {
    CreateOptimization,
    CompareSimulations,
    CreatePlanChooser,
    NgtorchRunChip,
    Simulation
  },
  methods: {
    ...mapActions('simulations', [
      'loadSimulations'
    ]),
    onCreateOption: function (id, period) {
      if (id === 'flexible') {
        this.createFlexibleDates(period)
      } else if (id === 'scratch') {
        this.driver.reset()
        this.baseSimulation = null
        this.dialog = true
      } else if (id === 'actualBudget') {
        this.createLastYearActualBudget()
      } else if (id === 'saturation') {
        this.createLastYearSaturationBudget()
      } else if (id === 'nextYear') {
        this.createNextYearBudgetPropagation()
      }
    },
    // Plan dates of a period picked in the "Flexible Dates" step: months become their first and
    // last day, kept inside the dates with data (flexibleBounds).
    flexibleDates: function (period) {
      var range = this.flexibleBounds
      var from = period.from
      var to = period.to
      if (this.modelFrequency !== 'week') {
        from = from + '-01'
        to = moment(to + '-01').endOf('month').format('YYYY-MM-DD')
      }
      return {
        dateFrom: from < range.start ? range.start : from,
        dateTo: to > range.end ? range.end : to
      }
    },
    // Same per-period values as the Calculator's Flexible Dates plan; the Optimizer keeps only
    // their total as the budget.
    flexiblePlan: function (period) {
      var dates = this.flexibleDates(period)
      var axis = planAxis(this.flexibleRange, dates.dateFrom, dates.dateTo, this.modelFrequency)
      return buildFlexibleMediaPlan(axis, Object.keys(this.channels), this.flexibleLookup, this.modelFrequency)
    },
    formatPeriodKey: function (key) {
      return this.modelFrequency === 'week' ? moment(key).format('ll') : moment(key + '-01').format('MMM YYYY')
    },
    flexibleSummary: function (period) {
      var plan = this.flexiblePlan(period)
      var unitKey = this.modelFrequency === 'week' ? 'views.planOptimizer.flexibleWeeks' : 'views.planOptimizer.flexibleMonths'
      var rows = []
      var sources = [
        { count: plan.counts.actual, icon: 'mdi-history', color: 'primary', label: 'views.planOptimizer.flexibleSummaryActual' },
        { count: plan.counts.propagation, icon: 'mdi-calendar-arrow-right', color: 'primary', label: 'views.planOptimizer.flexibleSummaryPropagation' },
        { count: plan.counts.empty, icon: 'mdi-circle-outline', color: 'grey', label: 'views.planOptimizer.flexibleSummaryEmpty' }
      ]
      sources.forEach(source => {
        if (source.count > 0) {
          rows.push({ icon: source.icon, color: source.color, label: this.$t(source.label), value: this.$tc(unitKey, source.count, { count: source.count }) })
        }
      })
      rows.push({
        icon: 'mdi-cash-multiple',
        color: 'primary',
        label: this.$t('views.planOptimizer.flexibleSummaryTotal'),
        value: this.formatValue(plan.total) + ' ' + this.spendUnitName,
        total: true
      })
      return rows
    },
    createFlexibleDates: function (period) {
      if (!period) return
      var dates = this.flexibleDates(period)
      this.baseSimulation = {
        name: this.$t('views.planOptimizer.createFlexibleDates'),
        date_from: dates.dateFrom,
        date_to: dates.dateTo,
        budget: this.flexiblePlan(period).total
      }
      this.isTemplate = true
      this.dialog = true
    },
    createLastYearActualBudget: function () {
      // Weekly models: mirror planSimulator's buildLastYearBase weekly branch —
      // anchor dateTo to the last Sunday at or before the model's last weekly
      // spend date, walk back 51 weeks (52-week window), and read from
      // media_spend_weekly. Falls through to the monthly logic below if the
      // model is monthly or weekly spend isn't available.
      if (this.modelFrequency === 'week' && this.hasMediaSpendWeeklyData) {
        var weeklySpend = this.mediaSpendWeeklyData
        var weeklyAnchorChannel = Object.keys(weeklySpend)[0]
        var weeklyAnchorDates = weeklySpend[weeklyAnchorChannel].x
        var weeklyDateToMoment = moment(weeklyAnchorDates[weeklyAnchorDates.length - 1])
        if (weeklyDateToMoment.day() !== 0) {
          weeklyDateToMoment.subtract(weeklyDateToMoment.day(), 'days')
        }
        var weeklyDateTo = weeklyDateToMoment.format('YYYY-MM-DD')
        var weeklyDateFrom = clampWeeklyStart(weeklyDateToMoment.clone().subtract(51, 'weeks').format('YYYY-MM-DD'), weeklyAnchorDates[0])

        var weeklyTotalBudget = 0
        var weeklyChannelNames = Object.keys(this.channels)
        weeklyChannelNames.forEach(function (channel) {
          if (weeklySpend[channel]) {
            var spendDates = weeklySpend[channel].x
            var spendValues = weeklySpend[channel].y
            for (var i = 0; i < spendDates.length; i++) {
              if (spendDates[i] >= weeklyDateFrom && spendDates[i] <= weeklyDateTo) {
                weeklyTotalBudget += Math.round(spendValues[i])
              }
            }
          }
        })

        this.baseSimulation = {
          name: this.$t('views.planOptimizer.createLastYearActualBudget'),
          date_from: weeklyDateFrom,
          date_to: weeklyDateTo,
          budget: weeklyTotalBudget
        }
        this.isTemplate = true
        this.dialog = true
        return
      }

      if (!this.hasMediaSpendData) return

      var mediaSpend = this.mediaSpendData
      var firstChannel = Object.keys(mediaSpend)[0]
      var allDatesFromSpend = mediaSpend[firstChannel].x
      var dateTo = allDatesFromSpend[allDatesFromSpend.length - 1]
      var dateFrom = clampMonthlyStart(moment(dateTo).subtract(11, 'months').startOf('month').format('YYYY-MM-DD'), allDatesFromSpend[0])
      var fromSliced = dateFrom.slice(0, 7)
      var toSliced = dateTo.slice(0, 7)

      var totalBudget = 0
      var channelNames = Object.keys(this.channels)
      channelNames.forEach(function (channel) {
        if (mediaSpend[channel]) {
          var spendDates = mediaSpend[channel].x
          var spendValues = mediaSpend[channel].y
          for (var i = 0; i < spendDates.length; i++) {
            var monthKey = spendDates[i].slice(0, 7)
            if (monthKey >= fromSliced && monthKey <= toSliced) {
              totalBudget += Math.round(spendValues[i])
            }
          }
        }
      })

      this.baseSimulation = {
        name: this.$t('views.planOptimizer.createLastYearActualBudget'),
        date_from: dateFrom,
        date_to: dateTo,
        budget: totalBudget
      }
      this.isTemplate = true
      this.dialog = true
    },
    createLastYearSaturationBudget: function () {
      if (!this.hasMediaSpendData || !this.hasSaturationData) return

      var mediaSpend = this.mediaSpendData
      var saturation = this.parsedModelReport.saturation
      var saturationGranularity = this.referenceModelConfig
        ? this.referenceModelConfig.saturation_granularity
        : null

      var monthlyMultiplier = saturationGranularity === 'W'
        ? (365.25 / 12 / 7)
        : 1

      var firstChannel = Object.keys(mediaSpend)[0]
      var allDatesFromSpend = mediaSpend[firstChannel].x
      var dateTo = allDatesFromSpend[allDatesFromSpend.length - 1]
      var dateFrom = clampMonthlyStart(moment(dateTo).subtract(11, 'months').startOf('month').format('YYYY-MM-DD'), allDatesFromSpend[0])
      var monthsCount = moment(dateTo).startOf('month').diff(moment(dateFrom).startOf('month'), 'months') + 1

      var totalBudget = 0
      var channelNames = Object.keys(this.channels)
      channelNames.forEach(function (channel) {
        if (saturation[channel] &&
            Array.isArray(saturation[channel].regions) &&
            saturation[channel].regions.length > 0) {
          var regions = saturation[channel].regions
          var cellValue = Math.round(regions[regions.length - 1] * monthlyMultiplier)
          totalBudget += cellValue * monthsCount
        }
      })

      this.baseSimulation = {
        name: this.$t('views.planOptimizer.createLastYearSaturationBudget'),
        date_from: dateFrom,
        date_to: dateTo,
        budget: totalBudget
      }
      this.isTemplate = true
      this.dialog = true
    },
    createNextYearBudgetPropagation: function () {
      var propagation = this.budgetPropagationData
      if (!propagation) return

      // Pick the granularity from the model's own frequency (matching what
      // planSimulator does via its split createNextYearBudgetPropagation
      // Monthly/Weekly callers) instead of the previous monthly-first auto
      // detection, which silently picked monthly on weekly models whenever
      // monthly propagation also happened to exist.
      var granularity = null
      if (this.modelFrequency === 'week' && this.hasBudgetPropagationWeeklyData) {
        granularity = 'weekly'
      } else if (this.hasBudgetPropagationMonthlyData) {
        granularity = 'monthly'
      } else if (this.hasBudgetPropagationWeeklyData) {
        granularity = 'weekly'
      }
      if (!granularity) return

      var dates = propagation.dates[granularity]
      var channelPropagation = propagation[granularity]

      var totalBudget = 0
      var channelNames = Object.keys(this.channels)
      channelNames.forEach(function (channel) {
        if (Array.isArray(channelPropagation[channel])) {
          for (var i = 0; i < channelPropagation[channel].length; i++) {
            totalBudget += Math.round(channelPropagation[channel][i])
          }
        }
      })

      this.baseSimulation = {
        name: this.$t('views.planOptimizer.createNextYearBudgetPropagation'),
        date_from: dates[0],
        date_to: dates[dates.length - 1],
        budget: totalBudget
      }
      this.isTemplate = true
      this.dialog = true
    },
    channelMismatch: function (base) {
      if (!base || !base.touchpoints) return null
      // Channels the plan operated on, derived from its saved (on) touchpoints. Only touchpoints that
      // were on are saved, so a model channel missing here may just have been off: the check can
      // only detect channels that left the model, not new ones.
      var planChannels = []
      var seen = {}
      if (Array.isArray(base.touchpoints)) {
        base.touchpoints.forEach(function (tp) {
          if (tp && tp.id && tp.id.indexOf('@div@') !== -1) {
            var ch = tp.id.slice(0, tp.id.indexOf('@div@'))
            if (!seen[ch]) { seen[ch] = true; planChannels.push(ch) }
          }
        })
      }
      var modelChannels = Object.keys(this.channels)
      var removed = planChannels.filter(function (c) { return modelChannels.indexOf(c) === -1 })
      if (removed.length === 0) return null
      return { removed: removed }
    },
    channelLabel: function (key) {
      if (this.currentClientObject && this.currentClientObject.pretty_names &&
          this.currentClientObject.pretty_names.media && this.currentClientObject.pretty_names.media[key]) {
        return this.currentClientObject.pretty_names.media[key]
      }
      if (this.channels[key] && this.channels[key].name) {
        return this.channels[key].name
      }
      return key
    },
    openMismatchWarning: function (base, isEdit, mismatch) {
      this.pendingBase = base
      this.pendingEdit = isEdit
      this.mismatchInfo = mismatch
      this.mismatchDialog = true
    },
    proceedWithMismatch: function () {
      this.mismatchDialog = false
      this.baseSimulation = this.pendingBase
      this.edit = this.pendingEdit
      this.dialog = true
      this.pendingBase = null
    },
    cloneSimulation: function (e) {
      var mismatch = this.channelMismatch(e.base)
      if (mismatch) {
        this.openMismatchWarning(e.base, false, mismatch)
        return
      }
      this.baseSimulation = e.base
      this.dialog = true
    },
    editSimulation: function (e) {
      var mismatch = this.channelMismatch(e.base)
      if (mismatch) {
        this.openMismatchWarning(e.base, true, mismatch)
        return
      }
      this.baseSimulation = e.base
      this.edit = true
      this.dialog = true
    },
    compareSimulation: function (e) {
      this.selectedSimulation = e.simulation
      this.compare = true
    },
    highlightCreateSimulation: function () {
      this.driver.highlight('#create-simulation')
    },
    formatDate (date) {
      return moment(date).format('ll')
    },
    formatDateFromNow (date) {
      return moment(date).fromNow()
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    budgetDisplay: function (item) {
      // For spend_up_to beta plans, item.budget is just a ceiling; show the real
      // invested amount as the main figure and the ceiling as a caption.
      var invested = investedBudget(item)
      if (isSpendUpTo(item) && invested !== null) {
        return {
          value: invested,
          caption: this.$t('views.planOptimizer.upTo', { value: this.formatValue(item.budget) })
        }
      }
      return { value: item.budget, caption: '' }
    },
    aggregateSalesDecompositionByPeriod (byPeriod, mode) {
      if (!byPeriod || typeof byPeriod !== 'object') {
        return { components: [], total: 0 }
      }
      const components = Object.keys(byPeriod).map(name => {
        const ys = (byPeriod[name] && Array.isArray(byPeriod[name].y)) ? byPeriod[name].y : []
        const sum = ys.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
        const value = mode === 'average' && ys.length > 0 ? sum / ys.length : sum
        return { name, value }
      })
      const total = components.reduce((a, c) => a + c.value, 0)
      return { components, total }
    },
    predictedSalesValue (item) {
      if (!item.results || !item.results.sales) return null
      const sales = item.results.sales
      let salesObj, outputName
      // The Total adds up every output, so it is not shown while any KPI is hidden: the column
      // then shows the first visible KPI instead (see listOutputName).
      const hideTotal = !!sales.outputs && hasHiddenKpis(this.currentClientObject)
      if (sales.total && !hideTotal) {
        salesObj = sales
        outputName = null
      } else if (sales.outputs) {
        outputName = firstVisibleOutputName(this.currentClientObject, sales.outputs)
        if (!outputName) return null
        salesObj = sales.outputs[outputName]
      } else {
        return null
      }
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      const isOutputAdditive = (name) => {
        if (!configOutputs || !configOutputs[name]) return true
        return configOutputs[name].is_additive !== false
      }
      let mode
      if (outputName) {
        mode = isOutputAdditive(outputName) ? 'sum' : 'average'
      } else {
        const realOutputs = sales.outputs ? Object.keys(sales.outputs) : []
        mode = realOutputs.every(name => isOutputAdditive(name)) ? 'sum' : 'average'
      }
      if (salesObj && salesObj.sales_decomposition_by_period) {
        return this.aggregateSalesDecompositionByPeriod(salesObj.sales_decomposition_by_period, mode).total
      }
      if (salesObj && salesObj.total) {
        return salesObj.total
      }
      return null
    },
    reportableSales: function (item) {
      const v = this.predictedSalesValue(item)
      return v == null ? null : this.formatValue(v)
    },
    customSort: function (items, sortBy, sortDesc, locale, customSorters) {
      const sortCol = sortBy[0]
      if (['date_created', 'date_from'].includes(sortCol)) {
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return new Date(a[sortCol]) - new Date(b[sortCol])
          })
        } else {
          items.sort((a, b) => {
            return new Date(b[sortCol]) - new Date(a[sortCol])
          })
        }
      } else if (sortCol === 'results.sales.total') {
        const dir = sortDesc[0] ? -1 : 1
        items.sort((a, b) => {
          const av = this.predictedSalesValue(a)
          const bv = this.predictedSalesValue(b)
          if (av == null && bv == null) return 0
          if (av == null) return 1
          if (bv == null) return -1
          return (av - bv) * dir
        })
      } else {
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return _.get(b, sortCol, '').toString().localeCompare(_.get(a, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        } else {
          items.sort((a, b) => {
            return _.get(a, sortCol, '').toString().localeCompare(_.get(b, sortCol, '').toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        }
      }
      return items
    },
    rowClick: function (item) {
      if (this.expandedSimulations.includes(item)) {
        const index = this.expandedSimulations.indexOf(item)
        this.$delete(this.expandedSimulations, index)
      } else {
        this.expandedSimulations.push(item)
      }
    }
  },
  computed: {
    ...mapGetters('clients', [
      'clients',
      'currentClient',
      'currentClientObject'
    ]),
    ...mapGetters('auth', [
      'authUser',
      'isExternalUser'
    ]),
    ...mapGetters('simulations', [
      'simulations',
      'simulationsLoading'
    ]),
    referenceModelConfig () {
      if (this.currentClientObject && this.currentClientObject.plan_reference_model) {
        return this.currentClientObject.plan_reference_model.config
      } else if (this.currentClientObject && this.currentClientObject.reference_model) {
        return this.currentClientObject.reference_model.config
      }
      return null
    },
    modelFrequency () {
      if (!this.referenceModelConfig) return 'month'
      return this.referenceModelConfig.saturation_granularity === 'W' ? 'week' : 'month'
    },
    parsedModelReport () {
      if (!this.currentClientObject) return null
      if (this.currentClientObject.model_report_storage) {
        return this.fetchedModelReport
      }
      if (!this.currentClientObject.model_report) return null
      var modelReport = this.currentClientObject.model_report
      if (typeof modelReport === 'string') {
        try {
          return JSON.parse(modelReport)
        } catch (e) {
          console.error('Failed to parse model_report:', e)
          return null
        }
      }
      return modelReport
    },
    modelReportFetchKey () {
      if (!this.currentClientObject || !this.currentClientObject.model_report_storage) return null
      const ts = this.currentClientObject.model_report_updated_at
      let tsKey = ''
      if (ts) {
        if (typeof ts.toMillis === 'function') tsKey = String(ts.toMillis())
        else if (ts.seconds !== undefined) tsKey = ts.seconds + '.' + (ts.nanoseconds || 0)
        else tsKey = String(ts)
      }
      return this.currentClientObject.model_report_storage + '@' + tsKey
    },
    // Dates the Optimizer form allows, for the "Flexible Dates" calendars (raw model start: the
    // Optimizer doesn't apply the legacy weekly push-forward).
    flexibleRange () {
      if (!this.currentClientObject || !this.referenceModelConfig) return null
      return calculatorRange(this.currentClientObject, { legacyWeeklyStart: false })
    },
    // Actual spend and budget propagation per period, at the model's frequency.
    flexibleLookup () {
      return buildPrefillLookup(this.parsedModelReport, this.modelFrequency)
    },
    // The part of flexibleRange the report has data for: the dates the "Flexible Dates" calendars allow.
    flexibleBounds () {
      return dataBounds(this.flexibleRange, this.flexibleLookup, this.modelFrequency)
    },
    // Date the "Flexible Dates" calendars open on, in their format (YYYY-MM or YYYY-MM-DD): the
    // start of the budget propagation when there is one, else of the last year of actual spend.
    // Null when the report has neither, which hides the template.
    flexibleView () {
      if (!this.flexibleBounds) return null
      var weekly = this.modelFrequency === 'week'
      var view = null
      var hasPropagation = weekly ? this.hasBudgetPropagationWeeklyData : this.hasBudgetPropagationMonthlyData
      if (hasPropagation) {
        view = this.budgetPropagationData.dates[weekly ? 'weekly' : 'monthly'][0]
      } else if (weekly && this.hasMediaSpendWeeklyData) {
        var weeklyDates = this.mediaSpendWeeklyData[Object.keys(this.mediaSpendWeeklyData)[0]].x
        view = moment(weeklyDates[weeklyDates.length - 1]).subtract(51, 'weeks').format('YYYY-MM-DD')
      } else if (!weekly && this.hasMediaSpendData) {
        var spendDates = this.mediaSpendData[Object.keys(this.mediaSpendData)[0]].x
        view = moment(spendDates[spendDates.length - 1]).subtract(11, 'months').format('YYYY-MM-DD')
      } else {
        return null
      }
      var range = this.flexibleBounds
      view = view < range.start ? range.start : (view > range.end ? range.end : view)
      return weekly ? view : view.slice(0, 7)
    },
    // Where the budget's values come from, under the "Flexible Dates" summary.
    flexibleHint () {
      var parts = []
      var actualKeys = Object.keys(this.flexibleLookup.actual).sort()
      var propagationKeys = Object.keys(this.flexibleLookup.propagation).sort()
      if (actualKeys.length > 0) {
        parts.push(this.$t('views.planOptimizer.flexibleHintActual', { date: this.formatPeriodKey(actualKeys[actualKeys.length - 1]) }))
      }
      if (propagationKeys.length > 0) {
        parts.push(this.$t('views.planOptimizer.flexibleHintPropagation', {
          from: this.formatPeriodKey(propagationKeys[0]),
          to: this.formatPeriodKey(propagationKeys[propagationKeys.length - 1])
        }))
      }
      return parts.join(' ')
    },
    // True while the Storage-hosted model report the templates are built from is being fetched.
    modelReportLoading () {
      return !!this.modelReportFetchKey && this.fetchedModelReportFor !== this.modelReportFetchKey
    },
    // Cards of the "Create Plan" modal, with the same availability rules the old dropdown had.
    createOptions () {
      var options = [{
        id: 'scratch',
        featured: true,
        icon: 'mdi-file-document-edit-outline',
        title: this.$t('views.planOptimizer.createFromScratch'),
        description: this.$t('views.planOptimizer.chooseFromScratchDescription')
      }]
      if (this.modelReportLoading) return options
      if (this.flexibleView) {
        var weekly = this.modelFrequency === 'week'
        options.push({
          id: 'flexible',
          icon: 'mdi-calendar-range',
          title: this.$t('views.planOptimizer.chooseFlexibleTitle'),
          description: this.$t('views.planOptimizer.chooseFlexibleDescription'),
          tag: this.$t('views.planOptimizer.chooseTagFlexible'),
          period: {
            type: weekly ? 'date' : 'month',
            min: weekly ? this.flexibleBounds.start : this.flexibleBounds.start.slice(0, 7),
            max: weekly ? this.flexibleBounds.end : this.flexibleBounds.end.slice(0, 7),
            // No period comes selected: the calendars only open where the data starts.
            view: this.flexibleView,
            hint: this.flexibleHint,
            summary: this.flexibleSummary
          }
        })
      }
      options.push({
        id: 'actualBudget',
        icon: 'mdi-history',
        title: this.$t('views.planOptimizer.chooseActualBudgetTitle'),
        description: this.$t('views.planOptimizer.chooseActualBudgetDescription'),
        tag: this.$t('views.planOptimizer.chooseTagStrategyCheck'),
        disabled: !this.hasMediaSpendData,
        disabledReason: this.$t('views.planOptimizer.chooseNeedsMediaSpend')
      })
      if (this.hasMediaSpendData && this.hasSaturationData) {
        options.push({
          id: 'saturation',
          icon: 'mdi-chart-bell-curve-cumulative',
          title: this.$t('views.planOptimizer.chooseSaturationTitle'),
          description: this.$t('views.planOptimizer.chooseSaturationDescription'),
          tag: this.$t('views.planOptimizer.chooseTagSaturation')
        })
      }
      if (this.hasBudgetPropagationData) {
        options.push({
          id: 'nextYear',
          icon: 'mdi-calendar-arrow-right',
          title: this.$t('views.planOptimizer.chooseNextYearTitle'),
          description: this.$t('views.planOptimizer.chooseNextYearDescription'),
          tag: this.$t('views.planOptimizer.chooseTagForwardLooking')
        })
      }
      return options
    },
    mediaSpendData () {
      if (!this.parsedModelReport || !this.parsedModelReport.v2 || !this.parsedModelReport.v2.media_spend) {
        return null
      }
      return this.parsedModelReport.v2.media_spend
    },
    hasMediaSpendData () {
      return this.mediaSpendData !== null && Object.keys(this.mediaSpendData).length > 0
    },
    mediaSpendWeeklyData () {
      if (!this.parsedModelReport || !this.parsedModelReport.v2 || !this.parsedModelReport.v2.media_spend_weekly) {
        return null
      }
      return this.parsedModelReport.v2.media_spend_weekly
    },
    hasMediaSpendWeeklyData () {
      return this.mediaSpendWeeklyData !== null && Object.keys(this.mediaSpendWeeklyData).length > 0
    },
    // Real per-channel scales from the model report (root-level marketing_scales),
    // fed to CreateOptimization for the beta optimizer feasibility check. Returns
    // null when absent/empty so the beta optimizer switch can be disabled.
    marketingScales () {
      if (!this.parsedModelReport || !this.parsedModelReport.marketing_scales) return null
      var scales = this.parsedModelReport.marketing_scales
      if (typeof scales !== 'object' || Object.keys(scales).length === 0) return null
      return scales
    },
    hasSaturationData () {
      if (!this.parsedModelReport || !this.parsedModelReport.saturation) return false
      var sat = this.parsedModelReport.saturation
      var keys = Object.keys(sat)
      if (keys.length === 0) return false
      return keys.some(function (ch) {
        return sat[ch] && Array.isArray(sat[ch].regions) && sat[ch].regions.length > 0
      })
    },
    budgetPropagationData () {
      if (!this.parsedModelReport || !this.parsedModelReport.v2 || !this.parsedModelReport.v2.budget_propagation) {
        return null
      }
      return this.parsedModelReport.v2.budget_propagation
    },
    hasBudgetPropagationData () {
      var p = this.budgetPropagationData
      if (!p || !p.dates) return false
      var hasMonthly = p.monthly && Object.keys(p.monthly).length > 0 &&
        Array.isArray(p.dates.monthly) && p.dates.monthly.length > 0
      var hasWeekly = p.weekly && Object.keys(p.weekly).length > 0 &&
        Array.isArray(p.dates.weekly) && p.dates.weekly.length > 0
      return !!(hasMonthly || hasWeekly)
    },
    hasBudgetPropagationMonthlyData () {
      var p = this.budgetPropagationData
      return !!(p && p.monthly && Object.keys(p.monthly).length > 0 &&
                p.dates && Array.isArray(p.dates.monthly) && p.dates.monthly.length > 0)
    },
    hasBudgetPropagationWeeklyData () {
      var p = this.budgetPropagationData
      return !!(p && p.weekly && Object.keys(p.weekly).length > 0 &&
                p.dates && Array.isArray(p.dates.weekly) && p.dates.weekly.length > 0)
    },
    customKpiName () {
      if (this.currentClientObject.customKpiName) {
        return this.currentClientObject.customKpiName
      } else {
        return this.$t('views.planOptimizer.defaultKpiName')
      }
    },
    ngtorchRunsPlans () {
      return this.optimizations.map(simulation => ({ ...simulation, clientId: this.currentClient }))
    },
    optimizations () {
      let optimizations = []
      for (let index = 0; index < this.simulations.length; index++) {
        // Hidden auto-generated Strategy Check plans live only in the What-If Scenario view.
        if (this.simulations[index].strategy_check) {
          continue
        }
        // Budget Simulator grid points show only inside their Budget Simulator.
        if (this.simulations[index].budget_simulation_id) {
          continue
        }
        if (!('type' in this.simulations[index]) || this.simulations[index].type === 'plan_optimization') {
          // Support stringified results, for supporting very large results objects (Firebase chokes on them and deletes the full simulation)
          if (this.simulations[index].results && typeof this.simulations[index].results === 'string') {
            let opti = { ...this.simulations[index] }
            opti.results = JSON.parse(opti.results)
            optimizations.push(opti)
          } else {
            optimizations.push({ ...this.simulations[index] })
          }
        }
      }
      if (this.isExternalUser && this.authUser && this.authUser.uid) {
        var uid = this.authUser.uid
        optimizations = optimizations.filter(function (opt) { return opt.created_by_id === uid || opt.showToExternalUsers })
      }
      return _.sortBy(optimizations, 'date_created').reverse()
    },
    parsedSimulations () {
      return _.sortBy(this.simulations.filter(simulation => !simulation.strategy_check && !simulation.budget_simulation_id && simulation.type !== 'budget_simulation').map(simulation => {
        // Support stringified results, for supporting very large results objects (Firebase chokes on them and deletes the full simulation)
        if (simulation.results && typeof simulation.results === 'string') {
          // Parse into a copy: assigning back onto the store object would make this computed drop the plan on its next evaluation
          return { ...simulation, results: JSON.parse(simulation.results) }
        }
        return simulation
      }), 'date_created').reverse()
    },
    channels () {
      if (this.currentClientObject && 'channels' in this.currentClientObject && this.currentClientObject.channels) {
        return this.currentClientObject.channels
      } else if (this.currentClientObject && this.currentClientObject.reference_model && this.currentClientObject.reference_model.config && this.currentClientObject.reference_model.config.media) {
        var channels = {}
        Object.keys(this.currentClientObject.reference_model.config.media).forEach(key => {
          channels[key] = {
            name: key
          }
        })
        return channels
      } else {
        return {}
      }
    },
    spendUnitName: function () {
      if (this.currentClientObject && this.currentClientObject.plan_reference_model && this.currentClientObject.plan_reference_model.config && 'invest_unit_name' in this.currentClientObject.plan_reference_model.config) {
        return this.currentClientObject.plan_reference_model.config.invest_unit_name
      } else if (this.currentClientObject && this.currentClientObject.reference_model && this.currentClientObject.reference_model.config && 'invest_unit_name' in this.currentClientObject.reference_model.config) {
        return this.currentClientObject.reference_model.config.invest_unit_name
      } else {
        return this.$t('views.planOptimizer.defaultCurrency')
      }
    },
    // The KPI the "Predicted" column shows while some KPI is hidden (the first visible output
    // of the config), or null when nothing is hidden and the column shows the Total.
    listOutputName: function () {
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      if (!configOutputs || !hasHiddenKpis(this.currentClientObject)) return null
      return firstVisibleOutputName(this.currentClientObject, configOutputs)
    },
    currentOutputUnitName: function () {
      if (this.listOutputName) {
        return this.referenceModelConfig.outputs[this.listOutputName].sales_unit_name || this.$t('views.planOptimizer.defaultCurrency')
      }
      if (this.currentClientObject && this.currentClientObject.reference_model && this.currentClientObject.reference_model.config && 'sales_unit_name' in this.currentClientObject.reference_model.config) {
        return this.currentClientObject.reference_model.config.sales_unit_name
      } else {
        return this.$t('views.planOptimizer.defaultCurrency')
      }
      /* if (this.simulations.length > 0 && this.simulations[0].results) {
        const sales = JSON.parse(this.simulations[0].results).sales
        if ('total' in sales) {
          return this.currentClientObject.reference_model.config.sales_unit_name || 'USD'
        } else if ('outputs' in sales) {
          return this.currentClientObject.reference_model.config.outputs[Object.keys(sales.outputs)[0]].sales_unit_name || 'USD'
        } else {
          return 'USD'
        }
      } else {
        return 'USD'
      } */
    },
    simulationHeaders: function () {
      return [
        {
          text: this.$t('views.planOptimizer.headerName'),
          align: 'start',
          sortable: true,
          value: 'name',
          width: '350px'
        },
        {
          text: this.$t('views.planOptimizer.headerBudget', { unit: this.spendUnitName }),
          sortable: true,
          value: 'budget'
        },
        {
          text: this.$t('views.planOptimizer.headerPredicted', { kpiName: this.customKpiName, unit: this.currentOutputUnitName }),
          sortable: true,
          value: 'results.sales.total'
        },
        {
          text: this.$t('views.planOptimizer.headerPeriod'),
          sortable: true,
          value: 'date_from',
          width: '220px'
        },
        {
          text: this.$t('views.planOptimizer.headerCreated'),
          sortable: true,
          value: 'date_created',
          width: '135px'
        }
      ]
    }
  },
  watch: {
    'currentClient': {
      immediate: true,
      handler () {
        this.loadSimulations({
          client: this.currentClient
        })
      }
    },
    modelReportFetchKey: {
      immediate: true,
      handler (newKey) {
        if (!newKey) {
          this.fetchedModelReport = null
          this.fetchedModelReportFor = null
          return
        }
        if (newKey === this.fetchedModelReportFor) return
        const target = newKey
        loadModelReportFromClient(this.currentClientObject)
          .then(report => {
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = report
              this.fetchedModelReportFor = target
            }
          })
          .catch(err => {
            console.error('Failed to load model_report from Storage:', err)
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = null
              this.fetchedModelReportFor = target
            }
          })
      }
    }
  }
}
